import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import contentService from '../services/contentService'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Badge } from '../components/ui/badge'
import { Progress } from '../components/ui/progress'
import { History, Play, Clock, Film } from 'lucide-react'

const WatchHistory = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [history, setHistory] = useState([])
  const [continueWatching, setContinueWatching] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (user) loadData()
  }, [user])

  const loadData = async () => {
    setIsLoading(true)
    try {
      const [historyData, continueData] = await Promise.all([
        contentService.getWatchHistory(user.id, 50),
        contentService.getContinueWatching(user.id, 10)
      ])
      setHistory(historyData)
      setContinueWatching(continueData)
    } catch (error) {
      console.error('Error loading history:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const formatDuration = (seconds) => {
    if (!seconds) return ''
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    if (h > 0) return `${h}h ${m}m`
    return `${m}m`
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now - date
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    return date.toLocaleDateString()
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <History className="w-16 h-16 mx-auto text-muted-foreground" />
            <h2 className="text-xl font-semibold">Sign in to see your history</h2>
            <Button onClick={() => navigate('/auth')} className="w-full">Sign In</Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <History className="w-8 h-8" />
            Watch History
          </h1>
          <p className="text-muted-foreground mt-1">{history.length} title{history.length !== 1 ? 's' : ''} in history</p>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-muted rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="space-y-8">
            {/* Continue Watching */}
            {continueWatching.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Play className="w-5 h-5" />
                    <span>Continue Watching</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {continueWatching.map((item) => (
                      <div
                        key={item.id}
                        className="group relative rounded-lg overflow-hidden border bg-card/50 hover:bg-card transition-colors cursor-pointer"
                        onClick={() => navigate(`/watch/${item.slug}`)}
                      >
                        <div className="relative aspect-video bg-muted">
                          {item.thumbnail ? (
                            <img src={item.thumbnail} alt={item.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Film className="w-8 h-8 text-muted-foreground" />
                            </div>
                          )}
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                            <Play className="w-10 h-10 text-white" />
                          </div>
                          <div className="absolute bottom-0 left-0 right-0">
                            <Progress value={item.progress} className="h-1 rounded-none" />
                          </div>
                        </div>
                        <div className="p-3">
                          <h4 className="font-medium truncate">{item.title}</h4>
                          <p className="text-sm text-muted-foreground">{item.progress}% watched</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Full History */}
            {history.length === 0 ? (
              <div className="text-center py-20">
                <History className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h2 className="text-xl font-semibold mb-2">No watch history yet</h2>
                <p className="text-muted-foreground mb-6">Start watching content to build your history.</p>
                <Button onClick={() => navigate('/browse')}>Browse Content</Button>
              </div>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Clock className="w-5 h-5" />
                    <span>All History</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {history.map((item, i) => (
                      <div
                        key={`${item.id}-${i}`}
                        className="flex items-center space-x-4 p-3 rounded-lg border bg-card/50 hover:bg-card transition-colors cursor-pointer"
                        onClick={() => navigate(`/watch/${item.slug}`)}
                      >
                        <div className="relative w-28 h-16 bg-muted rounded overflow-hidden flex-shrink-0">
                          {item.thumbnail ? (
                            <img src={item.thumbnail} alt={item.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Play className="w-6 h-6 text-muted-foreground" />
                            </div>
                          )}
                          {!item.completed && (
                            <div className="absolute bottom-0 left-0 right-0">
                              <Progress value={item.progress} className="h-1 rounded-none" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium truncate">{item.title}</h4>
                          {item.episodeTitle && (
                            <p className="text-sm text-muted-foreground truncate">
                              S{item.seasonNumber} E{item.episodeNumber} - {item.episodeTitle}
                            </p>
                          )}
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                            <span>{formatDuration(item.watchDuration)} watched</span>
                            <span>{formatDate(item.lastWatchedAt)}</span>
                          </div>
                        </div>
                        <Badge variant={item.completed ? 'default' : 'secondary'} className="flex-shrink-0">
                          {item.completed ? 'Completed' : `${item.progress}%`}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default WatchHistory
